import { useQuery } from "@tanstack/react-query";
import { useDates } from "dates/DatesContext";
import { useFetch } from "hooks/api";
import moment from "moment";
import { useAppSelector } from "redux/hooks";
import { getAdTypeName } from "utils/getAdTypeName";
import { apiClient } from "zodios/client";

const useAdTypeData = (categoryId, includeMetadata = false, additionalFilters, search) => {
  const channel = useAppSelector((state) => state.channel);
  const { start, end, preStart, preEnd } = useDates();
  const { marketPlace } = useAppSelector((state) => state.marketPlace);

  const startDate = moment(start).format("YYYY-MM-DD");
  const endDate = moment(end).format("YYYY-MM-DD");
  const preStartDate = moment(preStart).format("YYYY-MM-DD");
  const preEndDate = moment(preEnd).format("YYYY-MM-DD");

  const { data: adEntityData, isLoading: isAdEntityDataLoading } = useFetch(
    ["adEntityData", categoryId],
    "/api/data_report_v2/advertisements/entities/",
    {
      filters:
        categoryId ? `category_id:${parseInt(categoryId)},marketplace:${marketPlace}` : `marketplace:${marketPlace}`,
    },
    {
      default: [],
      enabled: includeMetadata,
    },
  );

  const params = {
    marketplace: marketPlace,
    start_date: moment(startDate).format("YYYY-MM-DD"),
    end_date: moment(endDate).format("YYYY-MM-DD"),
    start_date_previous: moment(preStartDate).format("YYYY-MM-DD"),
    end_date_previous: moment(preEndDate).format("YYYY-MM-DD"),
    filters: categoryId ? [{ id: "category_id", value: { op: "anyof", value: [categoryId.toString()] } }] : [],
  };

  if (additionalFilters) {
    params.filters = [...params.filters, ...additionalFilters];
  }

  // Remove ad_type filter from params since were grouping by ad_type
  params.filters = params.filters.filter((filter) => filter.id !== "ad_type");

  if (search) {
    params.search = search.split(",");
  }

  const { data: adTypeData, isLoading: isAdTypeDataLoading } = useQuery({
    queryKey: ["adTypeData", params],
    queryFn: async () => {
      const res = await apiClient.getV4AdvReportByAdType({ queries: params });
      return res;
    },
    default: [],
    select: (res) => {
      const formattedData = res?.data?.map((adTypeData) => ({
        ...adTypeData,
        name: getAdTypeName(adTypeData.ad_type, channel.currentChannel ?? "amazon"),
        ctr: adTypeData.click_thru_rate,
        ctr_delta: adTypeData.click_thru_rate_delta,
      }));
      return formattedData;
    },
  });

  const isLoading = isAdTypeDataLoading || isAdEntityDataLoading;

  return { data: adTypeData, metadata: adEntityData, isLoading };
};

export default useAdTypeData;
